import React, { useState, useMemo } from "react";
import { 
  Users, 
  Briefcase, 
  Sparkles, 
  Layers, 
  BookOpen, 
  FolderOpen, 
  ArrowRight,
  TrendingUp,
  Building2,
  Target,
  Award,
  AlertCircle,
  CheckCircle2,
  Search,
  SlidersHorizontal,
  ChevronRight,
  X
} from "lucide-react";
import type { OrgStats, DepartmentStat } from "../types";
import { MetricCards } from "../components/MetricCards";
import { CompactJoinLinkWidget } from "../components/CompactJoinLinkWidget";

interface OverviewPageProps {
  slug: string;
  stats: OrgStats;
  onNavigateTab: (tab: string) => void;
}

const getDepartmentGradient = (index: number) => {
  const gradients = [
    "from-indigo-600 to-blue-600 text-white",
    "from-purple-600 to-indigo-600 text-white",
    "from-emerald-600 to-teal-600 text-white",
    "from-amber-500 to-orange-600 text-white",
    "from-cyan-600 to-blue-600 text-white",
    "from-rose-500 to-pink-600 text-white",
  ];
  return gradients[index % gradients.length];
};

const getCompetencyTier = (score: number) => {
  if (score >= 80) return { label: "Advanced", color: "text-emerald-700 bg-emerald-50 border-emerald-200", bar: "from-emerald-500 to-teal-500", text: "text-emerald-600" };
  if (score >= 65) return { label: "Proficient", color: "text-indigo-700 bg-indigo-50 border-indigo-200", bar: "from-indigo-500 to-blue-500", text: "text-indigo-600" };
  if (score >= 50) return { label: "Developing", color: "text-amber-700 bg-amber-50 border-amber-200", bar: "from-amber-500 to-orange-500", text: "text-amber-600" };
  return { label: "Needs Focus", color: "text-rose-700 bg-rose-50 border-rose-200", bar: "from-rose-500 to-red-500", text: "text-rose-600" };
};

export const OverviewPage: React.FC<OverviewPageProps> = ({
  slug,
  stats,
  onNavigateTab,
}) => {
  const departmentStats: DepartmentStat[] = stats.department_stats || [];

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"competency" | "members" | "score" | "name">("competency");

  const filteredAndSortedStats = useMemo(() => {
    let list = [...departmentStats];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter((dept) => dept.department.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      if (sortBy === "competency") return b.competency_progress - a.competency_progress;
      if (sortBy === "members") return b.total_members - a.total_members;
      if (sortBy === "score") return b.avg_quiz_score - a.avg_quiz_score;
      if (sortBy === "name") return a.department.localeCompare(b.department);
      return 0;
    });
    return list;
  }, [departmentStats, searchQuery, sortBy]);

  // Compute top performers for quick summary pills
  const topDepartment = useMemo(() => {
    if (!departmentStats.length) return null;
    return [...departmentStats].sort((a, b) => b.competency_progress - a.competency_progress)[0];
  }, [departmentStats]);

  const mostEngagedDepartment = useMemo(() => {
    if (!departmentStats.length) return null;
    return [...departmentStats].sort((a, b) => {
      const rateA = a.total_members > 0 ? a.active_learners / a.total_members : 0;
      const rateB = b.total_members > 0 ? b.active_learners / b.total_members : 0;
      return rateB - rateA;
    })[0];
  }, [departmentStats]);

  const avgDeptCompetency = useMemo(() => {
    if (!departmentStats.length) return 0;
    const total = departmentStats.reduce((sum, d) => sum + (d.competency_progress || 0), 0);
    return Math.round(total / departmentStats.length);
  }, [departmentStats]);

  return (
    <div className="space-y-8">
      {/* Shareable B2B Join Link */}
      <CompactJoinLinkWidget slug={slug} stats={stats} />

      {/* KPI Cards & Seat Utilization Progress */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">Organization Performance & Capacity</h2>
            <p className="text-xs text-slate-500">Real-time engagement, proficiency, and quota metrics</p>
          </div>
        </div>
        <MetricCards stats={stats} />
      </div>

      {/* Quick Launchpad Navigation */}
      <div>
        <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-1">Administrative Launchpad</h2>
        <p className="text-xs text-slate-500 mb-4">Fast access to key operational centers and workforce learning workflows</p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => onNavigateTab("members")}
            className="group text-left p-5 bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md rounded-2xl transition duration-200 flex flex-col justify-between cursor-pointer"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:scale-105 transition">
                  <Users className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                  {stats.total_members} Members
                </span>
              </div>
              <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition text-sm">
                Team Roster & Invitations
              </h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Invite colleagues, manage department roles, view active learning status, or send bulk CSV invites.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center text-xs font-semibold text-indigo-600 gap-1 group-hover:gap-1.5 transition-all">
              Manage Team <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>

          <button
            onClick={() => onNavigateTab("workspaces")}
            className="group text-left p-5 bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md rounded-2xl transition duration-200 flex flex-col justify-between cursor-pointer"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center group-hover:scale-105 transition">
                  <Briefcase className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                  Multi-Team
                </span>
              </div>
              <h3 className="font-bold text-slate-900 group-hover:text-blue-600 transition text-sm">
                Workspaces & Divisions
              </h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Partition teams into autonomous workspaces with custom SOPs, bespoke AI courses, and scoped benchmarks.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center text-xs font-semibold text-blue-600 gap-1 group-hover:gap-1.5 transition-all">
              Explore Workspaces <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>

          <button
            onClick={() => onNavigateTab("intelligence")}
            className="group text-left p-5 bg-white border border-slate-200 hover:border-purple-300 hover:shadow-md rounded-2xl transition duration-200 flex flex-col justify-between cursor-pointer"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center group-hover:scale-105 transition">
                  <Sparkles className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md">
                  AI Engine
                </span>
              </div>
              <h3 className="font-bold text-slate-900 group-hover:text-purple-600 transition text-sm">
                Skill Intelligence & Gaps
              </h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                View team-wide competencies, analyze AI-detected weak skills, and launch automated learning interventions.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center text-xs font-semibold text-purple-600 gap-1 group-hover:gap-1.5 transition-all">
              View Matrix <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>

          <button
            onClick={() => onNavigateTab("assignments")}
            className="group text-left p-5 bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md rounded-2xl transition duration-200 flex flex-col justify-between cursor-pointer"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center group-hover:scale-105 transition">
                  <Layers className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-md">
                  SLA Rules
                </span>
              </div>
              <h3 className="font-bold text-slate-900 group-hover:text-amber-600 transition text-sm">
                Compliance & Learning Rules
              </h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Set mandatory completion deadlines, enforce passing score thresholds, and automate remedial quizzes.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center text-xs font-semibold text-amber-600 gap-1 group-hover:gap-1.5 transition-all">
              Configure Rules <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>

          <button
            onClick={() => onNavigateTab("documents")}
            className="group text-left p-5 bg-white border border-slate-200 hover:border-emerald-300 hover:shadow-md rounded-2xl transition duration-200 flex flex-col justify-between cursor-pointer"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center group-hover:scale-105 transition">
                  <FolderOpen className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                  RAG Vector Store
                </span>
              </div>
              <h3 className="font-bold text-slate-900 group-hover:text-emerald-600 transition text-sm">
                Company Knowledge & SOPs
              </h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Upload organizational policies and manuals. Power RAG retrieval and bespoke course generation.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center text-xs font-semibold text-emerald-600 gap-1 group-hover:gap-1.5 transition-all">
              Open Knowledge Base <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>

          <button
            onClick={() => onNavigateTab("courses")}
            className="group text-left p-5 bg-white border border-slate-200 hover:border-indigo-300 hover:shadow-md rounded-2xl transition duration-200 flex flex-col justify-between cursor-pointer"
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center group-hover:scale-105 transition">
                  <BookOpen className="w-5 h-5" />
                </div>
                <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md">
                  {stats.sponsored_courses?.length || 0} Courses
                </span>
              </div>
              <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition text-sm">
                Sponsored Training Catalog
              </h3>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                Explore sponsored enterprise courses and tracks available for your workforce to learn at no additional cost.
              </p>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 flex items-center text-xs font-semibold text-indigo-600 gap-1 group-hover:gap-1.5 transition-all">
              View Catalog <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </button>
        </div>
      </div>

      {/* Department Breakdown Section */}
      {departmentStats.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl p-8 sm:p-10 text-center shadow-xs">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mx-auto mb-3.5 shadow-2xs">
            <Building2 className="w-6 h-6" />
          </div>
          <h3 className="text-base font-bold text-slate-900">No Department Data Recorded</h3>
          <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 mb-5 leading-relaxed">
            Assign business unit or department designations to your organization members in the Team Roster to unlock comparative competency benchmarks, engagement rates, and targeted skill gap insights.
          </p>
          <button
            onClick={() => onNavigateTab("members")}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl transition duration-150 shadow-xs cursor-pointer"
          >
            Assign Member Departments <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-7 shadow-xs">
          {/* Section Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 pb-5 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-tr from-indigo-600 to-indigo-500 text-white flex items-center justify-center shadow-xs">
                  <Building2 className="w-4.5 h-4.5" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
                    Departmental Performance Overview
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Comparative metrics, competency benchmarks, and skill focus areas across organizational business units
                  </p>
                </div>
              </div>
            </div>

            {/* Filter and Search Controls */}
            <div className="flex flex-wrap items-center gap-2.5">
              <div className="relative min-w-[200px]">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search departments..."
                  className="w-full pl-8.5 pr-8 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl p-1 text-xs">
                <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400 ml-1.5 mr-0.5 shrink-0" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="bg-transparent text-xs font-semibold text-slate-700 focus:outline-none pr-2 py-0.5 cursor-pointer"
                >
                  <option value="competency">Sort: Competency</option>
                  <option value="members">Sort: Team Size</option>
                  <option value="score">Sort: Quiz Score</option>
                  <option value="name">Sort: Alphabetical</option>
                </select>
              </div>

              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-bold rounded-xl border border-slate-200/60 shrink-0">
                {departmentStats.length} {departmentStats.length === 1 ? "Unit" : "Units"}
              </span>
            </div>
          </div>

          {/* Aggregated KPI Summary Strip (when 2+ departments exist) */}
          {departmentStats.length >= 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <div className="bg-gradient-to-br from-indigo-50/70 to-white border border-indigo-100/90 rounded-2xl p-3.5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-100/80 text-indigo-700 flex items-center justify-center shrink-0">
                  <Award className="w-4.5 h-4.5" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-600 block">Top Competency</span>
                  <div className="text-sm font-bold text-slate-900 truncate">
                    {topDepartment?.department}
                  </div>
                  <span className="text-[11px] font-semibold text-indigo-700">
                    {topDepartment?.competency_progress}% target score
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-emerald-50/70 to-white border border-emerald-100/90 rounded-2xl p-3.5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-100/80 text-emerald-700 flex items-center justify-center shrink-0">
                  <TrendingUp className="w-4.5 h-4.5" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 block">Highest Engagement</span>
                  <div className="text-sm font-bold text-slate-900 truncate">
                    {mostEngagedDepartment?.department}
                  </div>
                  <span className="text-[11px] font-semibold text-emerald-700">
                    {mostEngagedDepartment?.total_members ? Math.min(100, Math.round((mostEngagedDepartment.active_learners / mostEngagedDepartment.total_members) * 100)) : 0}% active ratio
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-50/70 to-white border border-purple-100/90 rounded-2xl p-3.5 flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-100/80 text-purple-700 flex items-center justify-center shrink-0">
                  <Target className="w-4.5 h-4.5" />
                </div>
                <div className="min-w-0">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-purple-600 block">Org Benchmark</span>
                  <div className="text-sm font-bold text-slate-900">
                    {avgDeptCompetency}% Avg Competency
                  </div>
                  <span className="text-[11px] font-semibold text-purple-700">
                    Across {departmentStats.length} business units
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Department Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4.5">
            {filteredAndSortedStats.map((dept, idx) => {
              const participationRate = dept.total_members > 0 
                ? Math.min(100, Math.round((dept.active_learners / dept.total_members) * 100))
                : 0;
              const tier = getCompetencyTier(dept.competency_progress);
              const deptInitials = dept.department
                .split(/\s+/)
                .map((w) => w[0])
                .filter(Boolean)
                .slice(0, 2)
                .join("")
                .toUpperCase() || dept.department.slice(0, 2).toUpperCase();

              return (
                <div
                  key={dept.department}
                  className="bg-white border border-slate-200/90 hover:border-indigo-300 hover:shadow-md rounded-2xl p-5 flex flex-col justify-between transition-all duration-200 relative group overflow-hidden"
                >
                  {/* Top accent bar matching competency tier */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${tier.bar}`} />

                  <div>
                    {/* Card Header */}
                    <div className="flex items-start justify-between gap-3 mb-4">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${getDepartmentGradient(idx)} flex items-center justify-center font-bold text-xs tracking-wider shadow-xs shrink-0`}>
                          {deptInitials}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-sm sm:text-base text-slate-900 truncate group-hover:text-indigo-600 transition-colors" title={dept.department}>
                            {dept.department}
                          </h3>
                          <div className="flex items-center gap-1.5 text-xs text-slate-500">
                            <Users className="w-3.5 h-3.5 text-slate-400" />
                            <span>{dept.total_members} {dept.total_members === 1 ? "member" : "members"}</span>
                          </div>
                        </div>
                      </div>

                      {/* Activity Status Badge */}
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold shrink-0 ${
                        participationRate >= 70
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200/70"
                          : participationRate >= 40
                          ? "bg-blue-50 text-blue-700 border border-blue-200/70"
                          : "bg-amber-50 text-amber-700 border border-amber-200/70"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${
                          participationRate >= 70
                            ? "bg-emerald-500 animate-pulse"
                            : participationRate >= 40
                            ? "bg-blue-500"
                            : "bg-amber-500"
                        }`} />
                        {participationRate}% Active
                      </span>
                    </div>

                    {/* 2-Column Metrics Box */}
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100 flex flex-col justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Active Learners</span>
                        <div className="flex items-baseline justify-between mt-1">
                          <span className="text-lg font-black text-slate-900">{dept.active_learners}</span>
                          <span className="text-[11px] font-semibold text-emerald-600 flex items-center gap-0.5">
                            <TrendingUp className="w-3 h-3" />
                            {participationRate}%
                          </span>
                        </div>
                      </div>

                      <div className="bg-slate-50/80 rounded-xl p-2.5 border border-slate-100 flex flex-col justify-between">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Avg Quiz Score</span>
                        <div className="flex items-baseline justify-between mt-1">
                          <span className="text-lg font-black text-slate-900">{dept.avg_quiz_score}%</span>
                          <span className={`text-[11px] font-bold ${
                            dept.avg_quiz_score >= 80 ? "text-emerald-600" : dept.avg_quiz_score >= 65 ? "text-indigo-600" : "text-amber-600"
                          }`}>
                            {dept.avg_quiz_score >= 80 ? "High" : dept.avg_quiz_score >= 65 ? "Good" : "Needs Push"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Target Competency Progress */}
                    <div className="bg-slate-50/50 rounded-xl p-3 border border-slate-100 mb-4">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-700">
                          <Target className="w-3.5 h-3.5 text-indigo-600" />
                          Target Competency
                        </span>
                        <div className="flex items-center gap-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold border ${tier.color}`}>
                            {tier.label}
                          </span>
                          <span className={`text-xs font-black ${tier.text}`}>
                            {dept.competency_progress}%
                          </span>
                        </div>
                      </div>
                      <div className="w-full h-2 bg-slate-200/70 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-gradient-to-r ${tier.bar} transition-all duration-500`}
                          style={{ width: `${Math.min(100, Math.max(0, dept.competency_progress))}%` }}
                        />
                      </div>
                    </div>

                    {/* Weak Skills / Focus Areas */}
                    <div className="min-h-[46px]">
                      {dept.weak_skills && dept.weak_skills.length > 0 ? (
                        <div>
                          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3 text-rose-500" />
                            Key Focus Areas ({dept.weak_skills.length})
                          </span>
                          <div className="flex flex-wrap gap-1">
                            {dept.weak_skills.slice(0, 3).map((ws, i) => (
                              <span
                                key={i}
                                className="px-2 py-0.5 bg-rose-50 border border-rose-200/80 text-rose-700 text-[11px] font-medium rounded-md truncate max-w-[150px]"
                                title={ws}
                              >
                                {ws}
                              </span>
                            ))}
                            {dept.weak_skills.length > 3 && (
                              <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 text-[10px] font-bold rounded-md">
                                +{dept.weak_skills.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50/70 border border-emerald-200/60 rounded-lg px-2.5 py-2">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="text-[11px] font-medium">Competencies on track — no critical gaps</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Card Action Link */}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                    <button
                      onClick={() => onNavigateTab("members")}
                      className="text-xs font-semibold text-slate-600 hover:text-indigo-600 transition flex items-center gap-1 group/btn cursor-pointer"
                    >
                      <span>View department roster</span>
                      <ChevronRight className="w-3.5 h-3.5 text-slate-400 group-hover/btn:text-indigo-600 group-hover/btn:translate-x-0.5 transition" />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Empty Search Filter State */}
            {filteredAndSortedStats.length === 0 && (
              <div className="col-span-full py-12 text-center bg-slate-50/60 rounded-2xl border border-dashed border-slate-200">
                <Search className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm font-semibold text-slate-700">No departments matching &ldquo;{searchQuery}&rdquo;</p>
                <p className="text-xs text-slate-400 mt-0.5">Try searching for a different business unit or department name.</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="mt-3 px-3 py-1.5 text-xs font-semibold text-indigo-600 bg-white border border-slate-200 rounded-lg hover:border-indigo-300 shadow-2xs transition cursor-pointer"
                >
                  Clear Search Filter
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
